import * as React from 'react'
import { cn } from '@/lib/utils'
import type { ParseResult } from '@/lib/fileParser'
import { usePersistStore, defaultSP80022Params } from '@/stores/persistStore'
import { FileUploader } from '@/components/FileUploader'
import { ImportDataBanner } from '@/components/ImportDataBanner'
import { DataGenerator, type DataGeneratorMeta } from '@/components/DataGenerator'
import {
  DEFAULT_SP80022_PARAMS,
  SP80022_TEST_NAMES,
  MIN_BITS_SP80022,
  RECOMMENDED_BITS_SP80022,
  APP_VERSION,
} from '@/lib/constants'
import type {
  SP80022Parameters,
  SP80022TestResult,
  SP80022SuiteResult,
  ExportReport,
  InputFormat,
} from '@/types'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import { ExportButton } from '@/components/ExportButton'
import { ResultSummaryBar } from '@/components/ResultSummaryBar'
import { SnapshotManager } from '@/components/SnapshotManager'
import { DataExportMenu } from '@/components/DataExportMenu'
import { SectionHeader } from '@/components/SectionHeader'
import {
  Play,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Loader2,
  Clock,
  Info,
  FileText,
  Sliders,
  Shield,
  ChevronDown,
  TrendingUp,
} from 'lucide-react'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from 'recharts'
import { ChartExportButton } from '@/components/ChartExportButton'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBits(bits: number): string {
  if (bits >= 1_000_000) return `${(bits / 1_000_000).toFixed(2)}M bits`
  if (bits >= 1_000) return `${(bits / 1_000).toFixed(1)}K bits`
  return `${bits} bits`
}


function pValueColor(p: number, alpha: number): string {
  if (p >= alpha) return 'text-green-600 dark:text-green-400'
  if (p >= alpha / 10) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function pValueBgColor(p: number, alpha: number): string {
  if (p >= alpha) return 'bg-green-500/10'
  if (p >= alpha / 10) return 'bg-yellow-500/10'
  return 'bg-red-500/10'
}

function formatPValue(p: number): string {
  if (p < 0.0001) return p.toExponential(3)
  return p.toFixed(6)
}

type TestStatus = 'pending' | 'running' | 'passed' | 'failed'

// ---------------------------------------------------------------------------
// Parameter Input Component
// ---------------------------------------------------------------------------

interface ParamFieldProps {
  id: string
  label: React.ReactNode
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number | string
  tooltip?: string
  disabled: boolean
}

function ParamField({ id, label, value, onChange, min, max, step, tooltip, disabled }: ParamFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        {label}
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground/50 cursor-help">
                  <Info className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[200px]">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Label>
      <Input
        id={id}
        type="number"
        value={value}
        onChange={(e) => {
          const v = parseFloat(e.target.value)
          if (!isNaN(v)) onChange(v)
        }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className="h-9 font-mono text-sm"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline Test Progress List
// ---------------------------------------------------------------------------

interface ProgressItem {
  name: string
  status: TestStatus
  timeMs?: number
}

interface InlineProgressProps {
  items: ProgressItem[]
  overallProgress: number
}

function InlineTestProgress({ items, overallProgress }: InlineProgressProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Test Progress</span>
        <span className="text-sm font-mono text-muted-foreground">
          {Math.round(overallProgress)}%
        </span>
      </div>
      <Progress value={overallProgress} className="h-2" />
      <ul className="space-y-1">
        {items.map((item) => (
          <li
            key={item.name}
            className={cn(
              'flex items-center justify-between gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
              item.status === 'running' && 'bg-primary/5',
              item.status === 'pending' && 'text-muted-foreground'
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <StatusIcon status={item.status} />
              <span className={cn('truncate', item.status === 'running' && 'font-medium')}>
                {item.name}
              </span>
            </div>
            {item.timeMs != null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <Clock className="h-3 w-3" />
                <span className="font-mono">{item.timeMs.toFixed(0)}ms</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function StatusIcon({ status }: { status: TestStatus }) {
  switch (status) {
    case 'pending':
      return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/25" />
    case 'running':
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />
    case 'passed':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />
  }
}

// ---------------------------------------------------------------------------
// Test failure interpretation guidance
// ---------------------------------------------------------------------------

const TEST_INTERPRETATIONS: Record<string, string> = {
  'Frequency (Monobit)': 'The proportion of 0s and 1s is significantly different from 50/50. If your data is intentionally biased, use Tunable-Bias Analysis instead.',
  'Block Frequency': 'Some blocks have significantly uneven 0/1 ratios, suggesting local bias fluctuations.',
  'Runs': 'The number of uninterrupted runs of identical bits deviates from expected. This can indicate sequential dependence or insufficient mixing.',
  'Longest Run of Ones': 'The longest streak of consecutive 1s is unusual for random data. May indicate periodic patterns or insufficient entropy.',
  'Binary Matrix Rank': 'The rank distribution of bit matrices suggests linear dependence among bits. Common in LFSRs or poorly mixed generators.',
  'DFT (Spectral)': 'Periodic patterns detected in the frequency domain. The generator may have cyclic behavior.',
  'Non-overlapping Template': 'Certain bit patterns appear more or less frequently than expected. May indicate structural bias.',
  'Overlapping Template': 'The all-ones template appears with unusual frequency, suggesting systematic patterns.',
  "Maurer's Universal": 'The data is more compressible than expected, indicating insufficient entropy or redundancy.',
  'Linear Complexity': 'The linear complexity of bit blocks is abnormal, potentially indicating LFSR-like structure.',
  'Serial': 'Overlapping m-bit pattern frequencies deviate from expected, indicating non-uniform pattern distribution.',
  'Approximate Entropy': 'Adjacent-length pattern frequencies differ more than expected, suggesting predictability.',
  'Cumulative Sums': 'The cumulative sum of the sequence deviates too far from zero, indicating persistent bias.',
  'Random Excursions': 'The random walk visits certain states with abnormal frequency, suggesting non-random structure.',
  'Random Excursions Variant': 'State visit counts in the random walk are unusual, indicating deviations from randomness.',
}

// ---------------------------------------------------------------------------
// Single Result Card (inline, with multi-p-value support)
// ---------------------------------------------------------------------------

interface InlineResultCardProps {
  result: SP80022TestResult
  alpha: number
}

function InlineResultCard({ result, alpha }: InlineResultCardProps) {
  const [expanded, setExpanded] = React.useState(false)
  const isMultiPValue = result.pValues.length > 1
  const passCount = result.passed.filter(Boolean).length
  const failCount = result.passed.length - passCount

  // For display: use first p-value as representative, or min p-value for multi
  const representativePValue = isMultiPValue
    ? Math.min(...result.pValues)
    : result.pValues[0] ?? 0

  return (
    <Card className={cn(
      'transition-all',
      result.overallPassed
        ? 'border-green-500/20 dark:border-green-500/15'
        : 'border-red-500/30 dark:border-red-500/20'
    )}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {result.overallPassed ? (
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500 shrink-0" />
            )}
            <CardTitle className="text-sm font-medium truncate">
              {result.testName}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isMultiPValue && (
              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                {passCount}/{result.pValues.length}
              </Badge>
            )}
            <Badge variant={result.overallPassed ? 'success' : 'destructive'} className="text-[10px] px-2 py-0">
              {result.overallPassed ? 'PASS' : 'FAIL'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Statistic</p>
            <p className="font-mono text-xs">{result.statistic.toFixed(6)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              {isMultiPValue ? 'Min p-value' : 'p-value'}
            </p>
            <p className={cn('font-mono text-xs', pValueColor(representativePValue, alpha))}>
              {formatPValue(representativePValue)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Time</p>
            <p className="font-mono text-xs">{result.executionTimeMs.toFixed(0)}ms</p>
          </div>
        </div>

        {/* Multi-p-value detail expansion */}
        {isMultiPValue && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform',
                  expanded && 'rotate-180'
                )}
              />
              {expanded
                ? 'Hide sub-results'
                : `Show ${result.pValues.length} sub-results (${passCount} pass, ${failCount} fail)`}
            </button>
            {expanded && (
              <div className="mt-2 max-h-48 overflow-auto rounded-md border bg-muted/30">
                <div className="flex justify-end px-2 pt-1">
                  <DataExportMenu
                    data={result.pValues.map((pv, i) => ({
                      index: i + 1,
                      pValue: pv,
                      passed: result.passed[i] ?? false,
                    }))}
                    fileName={`${result.testName.toLowerCase().replace(/\s+/g, '-')}-pvalues`}
                    columns={['index', 'pValue', 'passed']}
                    title={`${result.testName} Sub-Results`}
                  />
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-2 py-1.5 text-left font-medium">#</th>
                      <th className="px-2 py-1.5 text-right font-medium">p-value</th>
                      <th className="px-2 py-1.5 text-center font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.pValues.map((pv, i) => (
                      <tr
                        key={i}
                        className={cn(
                          'border-b border-muted/50 last:border-0',
                          pValueBgColor(pv, alpha)
                        )}
                      >
                        <td className="px-2 py-1 font-mono text-muted-foreground">{i + 1}</td>
                        <td className={cn('px-2 py-1 text-right font-mono', pValueColor(pv, alpha))}>
                          {formatPValue(pv)}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {result.passed[i] ? (
                            <CheckCircle2 className="h-3 w-3 text-green-500 inline" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-500 inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Failure interpretation */}
        {!result.overallPassed && TEST_INTERPRETATIONS[result.testName] && (
          <div className="mt-3 flex items-start gap-1.5 rounded bg-red-500/5 p-2">
            <Info className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {TEST_INTERPRETATIONS[result.testName]}
            </p>
          </div>
        )}

        {/* Notes */}
        {result.notes && (
          <p className="mt-2 text-[10px] text-muted-foreground italic">{result.notes}</p>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Summary Bar
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// P-Value Summary Chart (log scale bar chart)
// ---------------------------------------------------------------------------

/** Short labels for the 15 tests */
const SHORT_NAMES: Record<string, string> = {
  'Frequency (Monobit)': 'Freq',
  'Block Frequency': 'BlkFreq',
  'Runs': 'Runs',
  'Longest Run of Ones': 'LongRun',
  'Binary Matrix Rank': 'Rank',
  'DFT (Spectral)': 'DFT',
  'Non-Overlapping Template': 'NonOvl',
  'Overlapping Template': 'Overlap',
  "Maurer's Universal Statistical": 'Univ',
  'Linear Complexity': 'LinCpx',
  'Serial': 'Serial',
  'Approximate Entropy': 'ApEn',
  'Cumulative Sums': 'CumSum',
  'Random Excursions': 'RndExc',
  'Random Excursions Variant': 'RndExV',
}

interface PValueChartProps {
  tests: SP80022TestResult[]
  alpha: number
}

function PValueChart({ tests, alpha }: PValueChartProps) {
  const chartRef = React.useRef<HTMLDivElement>(null)

  // Detect dark mode and use appropriate colors for SVG (Recharts doesn't support oklch)
  const isDark = document.documentElement.classList.contains('dark')
  const colors = {
    muted: isDark ? '#a1a1aa' : '#71717a',   // zinc-400 / zinc-500
    border: isDark ? '#3f3f46' : '#e4e4e7',   // zinc-700 / zinc-200
    accent: isDark ? '#27272a' : '#f4f4f5',   // zinc-800 / zinc-100
  }

  const plotData = tests.map((t, i) => {
    let pVal: number
    if (t.pValues.length <= 2) {
      pVal = t.pValues[0] ?? 0
    } else {
      const sorted = [...t.pValues].sort((a, b) => a - b)
      pVal = sorted[Math.floor(sorted.length / 2)]!
    }
    return {
      x: i,
      name: SHORT_NAMES[t.testName] ?? t.testName.substring(0, 6),
      fullName: t.testName,
      pValue: Math.max(pVal, 1e-10),
      passed: t.overallPassed,
      isMulti: t.pValues.length > 2,
    }
  })

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null
    const d = payload[0].payload
    return (
      <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
        <p className="font-medium">{d.fullName}</p>
        <p className="font-mono mt-0.5">
          {d.isMulti ? 'median ' : ''}p = {d.pValue < 0.0001 ? d.pValue.toExponential(3) : d.pValue.toFixed(6)}
        </p>
        <p className={d.passed ? 'text-green-500' : 'text-red-500'}>{d.passed ? 'PASS' : 'FAIL'}</p>
      </div>
    )
  }

  // Y-axis: log scale showing p-values. 1 at top, small values at bottom.
  // We plot log10(p) directly and use scale="log" on YAxis.
  const yTicks = [1, 0.1, 0.01, 0.001, 0.0001]

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">P-Value Summary <span className="text-[10px] font-normal text-muted-foreground ml-1">(log scale)</span></CardTitle>
          <ChartExportButton containerRef={chartRef} fileName="sp800-22-pvalues" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-2 sm:px-4">
        <div ref={chartRef} role="img" aria-label="P-value summary chart showing test results on a logarithmic scale. Points above the significance threshold line indicate passing tests.">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 12, right: 12, bottom: 50, left: 4 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.5}
                  horizontal
                  vertical={false}
                />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={[-0.5, plotData.length - 0.5]}
                  ticks={plotData.map((_, i) => i)}
                  tickFormatter={(i: number) => plotData[i]?.name ?? ''}
                  tick={{ fontSize: 9, fill: colors.muted }}
                  tickLine={false}
                  axisLine={{ stroke: colors.border }}
                  angle={-40}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  dataKey="pValue"
                  type="number"
                  scale="log"
                  domain={[
                    Math.min(0.0001, ...plotData.map(d => d.pValue)) / 2,
                    1,
                  ]}
                  ticks={yTicks}
                  tickFormatter={(v: number) => v >= 0.01 ? v.toString() : v.toExponential(0)}
                  tick={{ fontSize: 9, fill: colors.muted }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <ReferenceLine
                  y={alpha}
                  stroke="#ef4444"
                  strokeDasharray="6 3"
                  strokeWidth={2}
                  label={{
                    value: `\u03B1 = ${alpha}`,
                    position: 'insideBottomLeft',
                    style: { fontSize: 9, fill: '#ef4444', fontWeight: 600 },
                    offset: 4,
                  }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Scatter data={plotData} isAnimationActive={false}>
                  {plotData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.passed ? '#22c55e' : '#ef4444'}
                      r={6}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 px-2">
          Each dot is one test's p-value (log scale). Dots below the {'\u03B1'} = {alpha} line indicate failure.
          Multi-p-value tests show median.
        </p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function NIST80022Page() {
  // -- Store state --
  const persist = usePersistStore()
  const {
    sp80022Params: persistedParams,
    sp80022Result: persistedResult,
    setSP80022Params: persistParams,
    setSP80022Result: persistResult,
    clearSP80022Result,
  } = persist

  // Per-module input data (persisted)
  const [inputData, setInputDataLocal] = React.useState<ParseResult | null>(
    () => persist.getModuleInput('sp80022')
  )
  const setInputData = React.useCallback((data: ParseResult) => {
    setInputDataLocal(data)
    persist.setModuleInput('sp80022', data)
  }, [persist])

  // -- Parameters (initialize from persist store, fallback to defaults) --
  const [params, setParams] = React.useState<SP80022Parameters>(
    persistedParams ?? { ...DEFAULT_SP80022_PARAMS },
  )

  // -- Execution state --
  const [isRunning, setIsRunning] = React.useState(false)
  const [currentTestName, setCurrentTestName] = React.useState<string | null>(null)
  const [progressResults, setProgressResults] = React.useState<SP80022TestResult[]>([])
  const [overallProgress, setOverallProgress] = React.useState(0)
  // suiteResult: driven by persist store, local override only while running
  // IMPORTANT: Do NOT clear persist store from setSuiteResult — only explicit close/reset should clear it
  const [localSuiteResult, setLocalSuiteResult] = React.useState<SP80022SuiteResult | null>(null)
  const suiteResult = localSuiteResult ?? persistedResult ?? null
  const setSuiteResult = React.useCallback((r: SP80022SuiteResult | null) => {
    setLocalSuiteResult(r)
    if (r) persistResult(r)
    else clearSP80022Result()
  }, [persistResult, clearSP80022Result])

  const [error, setError] = React.useState<string | null>(null)

  // Sync params to persist store
  React.useEffect(() => {
    persistParams(params)
  }, [params, persistParams])

  // Restore defaults handler
  const restoreDefaults = React.useCallback(() => {
    setParams({ ...defaultSP80022Params })
  }, [])

  // -- Worker ref --
  const workerRef = React.useRef<Worker | null>(null)

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
    }
  }, [])

  // -- Derived state --
  const hasBinaryInput = inputData?.kind === 'binary'
  const bitLength = hasBinaryInput ? inputData.input.bitLength : 0
  const canRun = hasBinaryInput && bitLength >= 100 && !isRunning

  // -- Estimated bias for mismatch warning --
  const estimatedBias = React.useMemo(() => {
    if (!hasBinaryInput || !inputData) return null
    const data = inputData.input.data
    const n = inputData.input.bitLength
    let ones = 0
    for (let i = 0; i < n; i++) {
      ones += (data[i >>> 3] >> (7 - (i & 7))) & 1
    }
    return ones / n
  }, [hasBinaryInput, inputData])
  const isBiased = estimatedBias !== null && Math.abs(estimatedBias - 0.5) > 0.05

  // -- Handle generated data from DataGenerator --
  const handleGeneratedData = React.useCallback(
    (data: Uint8Array | Float64Array, meta: DataGeneratorMeta) => {
      if (!(data instanceof Uint8Array) || !meta.bitLength) return
      setInputData({
        kind: 'binary',
        input: {
          data,
          bitLength: meta.bitLength,
          sampleCount: meta.bitLength,
          fileName: `generated-${meta.description.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
          fileSize: data.byteLength,
          format: 'binary' as InputFormat,
        },
      })
      setSuiteResult(null)
      setProgressResults([])
      setOverallProgress(0)
      setCurrentTestName(null)
      setError(null)
    },
    [setInputData],
  )

  // When new data is loaded via FileUploader, persist and clear old results
  const handleDataLoaded = React.useCallback((result: ParseResult) => {
    setInputDataLocal(result)
    persist.setModuleInput('sp80022', result)
    setLocalSuiteResult(null)
    setProgressResults([])
    setOverallProgress(0)
    setCurrentTestName(null)
    setError(null)
  }, [])

  // Close results (dismiss results panel)
  const handleCloseResults = React.useCallback(() => {
    setLocalSuiteResult(null)
    clearSP80022Result()
    setProgressResults([])
    setOverallProgress(0)
  }, [clearSP80022Result])

  const handleReset = React.useCallback(() => {
    // Terminate any running worker
    if (workerRef.current) {
      workerRef.current.terminate()
      workerRef.current = null
    }
    setInputDataLocal(null)
    persist.clearModuleInput('sp80022')
    setParams({ ...DEFAULT_SP80022_PARAMS })
    setIsRunning(false)
    setCurrentTestName(null)
    setProgressResults([])
    setOverallProgress(0)
    setLocalSuiteResult(null)
    clearSP80022Result()
    setError(null)
  }, [persist, clearSP80022Result])

  const handleRunTests = React.useCallback(() => {
    if (!inputData || inputData.kind !== 'binary') return

    // Instantly jump to top, then state updates — avoids layout-shift jitter
    window.scrollTo(0, 0)

    // Terminate any existing worker
    if (workerRef.current) {
      workerRef.current.terminate()
      workerRef.current = null
    }

    // Reset execution state
    setIsRunning(true)
    setCurrentTestName(null)
    setProgressResults([])
    setOverallProgress(0)
    setSuiteResult(null)
    setError(null)

    const worker = new Worker(
      new URL('@/engine/workers/sp80022Worker.ts', import.meta.url),
      { type: 'module' }
    )

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data
      switch (msg.type) {
        case 'progress': {
          const testResult = msg.result as SP80022TestResult
          const testIndex = msg.testIndex as number
          const totalTests = msg.totalTests as number

          setCurrentTestName(msg.testName)
          setProgressResults((prev) => [...prev, testResult])
          setOverallProgress(((testIndex + 1) / totalTests) * 100)
          break
        }
        case 'complete': {
          const result = msg.result as SP80022SuiteResult
          setSuiteResult(result)
          persistResult(result)
          setIsRunning(false)
          setCurrentTestName(null)
          setOverallProgress(100)
          workerRef.current = null
          break
        }
        case 'error': {
          setError(msg.error as string)
          setIsRunning(false)
          setCurrentTestName(null)
          workerRef.current = null
          break
        }
      }
    }

    worker.onerror = (e) => {
      setError(e.message || 'An unexpected worker error occurred.')
      setIsRunning(false)
      setCurrentTestName(null)
      workerRef.current = null
    }

    workerRef.current = worker

    // Copy the data buffer before transferring (transferring invalidates the original)
    const originalData = inputData.input.data
    const bufferCopy = originalData.buffer.slice(
      originalData.byteOffset,
      originalData.byteOffset + originalData.byteLength
    )

    worker.postMessage(
      {
        type: 'run',
        data: bufferCopy,
        bitLength: inputData.input.bitLength,
        params,
      },
      [bufferCopy]
    )
  }, [inputData, params, persistResult])

  // -- Build progress items --
  const progressItems: ProgressItem[] = React.useMemo(() => {
    return SP80022_TEST_NAMES.map((name) => {
      const result = progressResults.find((r) => r.testName === name)
      if (result) {
        return {
          name,
          status: (result.overallPassed ? 'passed' : 'failed') as TestStatus,
          timeMs: result.executionTimeMs,
        }
      }
      if (currentTestName === name) {
        return { name, status: 'running' as TestStatus }
      }
      return { name, status: 'pending' as TestStatus }
    })
  }, [progressResults, currentTestName])

  // -- Build export report --
  const exportReport: ExportReport | null = React.useMemo(() => {
    if (!suiteResult || !inputData || inputData.kind !== 'binary') return null
    return {
      version: APP_VERSION,
      timestamp: new Date().toISOString(),
      inputFile: {
        name: inputData.input.fileName ?? 'unknown',
        size: inputData.input.fileSize,
        sha256: inputData.input.sha256 ?? '',
        sampleCount: inputData.input.sampleCount,
        format: inputData.input.format,
      },
      results: {
        sp80022: suiteResult,
      },
    }
  }, [suiteResult, inputData])

  // -- Determine right panel content --
  const showInitial = !isRunning && progressResults.length === 0 && !suiteResult && !error
  const showProgress = isRunning
  const showResults = !isRunning && suiteResult != null

  // Use completed results for result cards (from suite result for consistency)
  const displayResults = suiteResult?.tests ?? progressResults

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
      {/* Header */}
      <h1 className="text-2xl font-bold mb-2">NIST SP 800-22 Rev. 1a</h1>
      <p className="text-muted-foreground mb-8">
        Statistical test suite for random and pseudorandom number generators — 15 tests for binary bitstreams.
      </p>

      {/* Responsive grid: sidebar + main */}
      <div className="grid lg:grid-cols-[380px_1fr] gap-8">

        {/* ============================================================= */}
        {/* LEFT PANEL: Input & Parameters                                 */}
        {/* ============================================================= */}
        <div className="space-y-6 min-w-0">
          <div className="flex items-center gap-2">
            <SectionHeader title="Input" icon={FileText} />
          </div>

          {/* File Upload */}
          <FileUploader
            acceptedKinds={['binary']}
            formatHints="binary"
            kindMismatchMessage="This module requires binary data. For continuous data, use the Gaussian page with PIT Bridge."
            onDataLoaded={handleDataLoaded}
            moduleData={inputData}
            onModuleClear={() => { setInputDataLocal(null); persist.clearModuleInput('sp80022') }}
          />

          {/* Import from other modules */}
          {!inputData && (
            <ImportDataBanner
              currentModule="sp80022"
              acceptedKinds={['binary']}
              onImport={setInputData}
            />
          )}

          {/* Bias mismatch warning */}
          {isBiased && estimatedBias !== null && (
            <div className="flex items-start gap-2.5 rounded-md bg-yellow-500/10 border border-yellow-500/30 p-3">
              <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              <div className="text-xs text-yellow-600 dark:text-yellow-400">
                <p className="font-medium mb-1">Biased data detected</p>
                <p>
                  Your data has P(1) &asymp; {estimatedBias.toFixed(3)}. SP 800-22 assumes unbiased data
                  (P(1) = 0.5), so most tests will fail. Consider using{' '}
                  <a href="/tunable-bias" className="underline font-medium hover:text-yellow-500">
                    Tunable-Bias Analysis
                  </a>
                  {' '}with target bias p&#8320; = {estimatedBias.toFixed(3)} instead.
                </p>
              </div>
            </div>
          )}

          {/* OR separator + Data Generator */}
          <div className="relative flex items-center justify-center">
            <Separator className="flex-1" />
            <span className="px-3 text-xs font-medium text-muted-foreground uppercase">
              or
            </span>
            <Separator className="flex-1" />
          </div>

          <DataGenerator mode="binary" onDataGenerated={handleGeneratedData} />

          <SectionHeader title="Parameters" icon={Sliders} />

          {/* Parameters */}
          <Card role="region" aria-label="Test parameters">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Parameters</CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={restoreDefaults} disabled={isRunning}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Defaults
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <ParamField
                id="alpha"
                label={<>Significance level <span className="italic">α</span></>}
                value={params.alpha}
                onChange={(v) => setParams((p) => ({ ...p, alpha: v }))}
                min={0.001}
                max={0.05}
                step="0.001"
                tooltip="Reject H₀ if p-value < α"
                disabled={isRunning}
              />
              <Separator />
              <ParamField
                id="blockSizeFreq"
                label={<>Block Frequency <span className="italic">M</span></>}
                value={params.blockSizeFreq}
                onChange={(v) => setParams((p) => ({ ...p, blockSizeFreq: Math.round(v) }))}
                min={20}
                max={10000}
                step={1}
                tooltip="Block size for Test 2 (Frequency within a Block)"
                disabled={isRunning}
              />
              <ParamField
                id="templateLength"
                label={<>Template length <span className="italic">m</span></>}
                value={params.templateLength}
                onChange={(v) => setParams((p) => ({ ...p, templateLength: Math.round(v) }))}
                min={2}
                max={16}
                step={1}
                tooltip="Template length for Tests 7 and 8"
                disabled={isRunning}
              />
              <ParamField
                id="blockSizeLinear"
                label={<>Linear Complexity <span className="italic">M</span></>}
                value={params.blockSizeLinear}
                onChange={(v) => setParams((p) => ({ ...p, blockSizeLinear: Math.round(v) }))}
                min={100}
                max={5000}
                step={1}
                tooltip="Block size for Test 10 (Linear Complexity)"
                disabled={isRunning}
              />
              <ParamField
                id="patternLength"
                label={<>Pattern length <span className="italic">m</span></>}
                value={params.patternLength}
                onChange={(v) => setParams((p) => ({ ...p, patternLength: Math.round(v) }))}
                min={2}
                max={24}
                step={1}
                tooltip="Pattern length for Tests 11 and 12 (Serial, ApEn)"
                disabled={isRunning}
              />
              <Separator />

              {/* Run / Reset Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleRunTests}
                  disabled={!canRun}
                  className="flex-1"
                  size="lg"
                  data-testid="run-tests-btn"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Run Tests
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleReset}
                  disabled={isRunning}
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Info note */}
          <div className="flex gap-2 rounded-md bg-primary/5 p-3">
            <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Requires binary bitstream data. Recommended: {formatBits(RECOMMENDED_BITS_SP80022)}. Minimum: {formatBits(MIN_BITS_SP80022)}.
            </p>
          </div>
        </div>

        {/* ============================================================= */}
        {/* RIGHT PANEL: Results                                           */}
        {/* ============================================================= */}
        <div className="space-y-6 min-w-0">
          <SectionHeader
            title="Results"
            icon={TrendingUp}
            right={
              showResults && suiteResult ? (
                <>
                  {exportReport && <ExportButton report={exportReport} />}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCloseResults}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : undefined
            }
          />

          {/* Error display */}
          {error && (
            <Card className="border-destructive/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Test execution failed</p>
                    <p className="text-sm text-muted-foreground mt-1">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Initial state: empty state with test overview */}
          {showInitial && (
            <Card className="border-dashed">
              <CardContent className="p-12">
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="rounded-full bg-primary/10 p-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Ready for Testing</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Upload a binary bitstream and click &ldquo;Run Tests&rdquo; to execute the full NIST SP 800-22 suite.
                      Results will appear progressively as each test completes.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 w-full max-w-lg">
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />
                        <p className="text-xs font-medium">Tests 1–6</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Frequency, runs, rank, and spectral analysis</p>
                    </div>
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <p className="text-xs font-medium">Tests 7–12</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Template matching, complexity, serial, and entropy</p>
                    </div>
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-orange-500" />
                        <p className="text-xs font-medium">Tests 13–15</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Cumulative sums and random excursions</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Running: show progress */}
          {showProgress && (
            <Card>
              <CardContent className="p-4">
                <InlineTestProgress
                  items={progressItems}
                  overallProgress={overallProgress}
                />
              </CardContent>
            </Card>
          )}

          {/* Completed: summary + results */}
          {showResults && suiteResult && (
            <>
              {/* Summary bar */}
              <ResultSummaryBar
                passCount={suiteResult.passCount}
                failCount={suiteResult.failCount}
                totalTests={suiteResult.totalTests}
                overallPassed={suiteResult.overallPassed}
                executionTimeMs={suiteResult.executionTimeMs}
                passMessage="Sequence appears random"
                failMessage="Sequence may not be random"
                detail={`\u03B1 = ${params.alpha}`}
              />

              {/* Snapshot save/compare */}
              <SnapshotManager module="sp80022" hasResult={!!suiteResult} onLoadSnapshot={(snap) => {
                setLocalSuiteResult(snap.result)
                setProgressResults(snap.result.tests ?? [])
                setOverallProgress(100)
              }} />

              {/* P-value summary chart */}
              <PValueChart tests={suiteResult.tests} alpha={params.alpha} />

              {/* Detailed results label */}
              <p className="text-sm text-muted-foreground">
                Detailed results for each of the 15 tests:
              </p>

              {/* Result cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {displayResults.map((result: SP80022TestResult) => (
                  <InlineResultCard
                    key={result.testName}
                    result={result}
                    alpha={params.alpha}
                  />
                ))}
              </div>
            </>
          )}

          {/* Running but partial results: show cards below progress */}
          {showProgress && progressResults.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {progressResults.map((result) => (
                <InlineResultCard
                  key={result.testName}
                  result={result}
                  alpha={params.alpha}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
